import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearCart } from '../store/cartSlice.js';
import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import Bill from './Bill.js';
import { downloadBillAsPDF } from '../utils/billUtils.js';
import { sendPaymentEmail } from '../utils/emailService.js';

const OrderSuccess = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [showBill, setShowBill] = useState(false);
  const [orderData, setOrderData] = useState(null);

  const emailSentRef = useRef(false);

  useEffect(() => {
    if (!location.state?.paymentVerified) {
      toast.error("Invalid access. Redirecting to home.");
      navigate("/");
      return;
    }
    dispatch(clearCart());
    const order = location.state?.orderData;
    if (!order) {
      toast.error("Order data not found.");
      navigate("/");
      return;
    }
    setOrderData(order);
    toast.success("Order placed successfully!");

    if (!emailSentRef.current && order.email) {
      emailSentRef.current = true;
      sendPaymentEmail(order).then((result) => {
        if (result.success) toast.info("📧 Order confirmation sent to " + order.email);
      });
    }
  }, [dispatch, location.state, navigate]);

  const handleDownloadBill = () => {
    if (orderData) {
      downloadBillAsPDF(orderData.orderId);
    }
  };

  return (
    <div className="container py-5">
      <style>{`
        .success-animation {
          animation: scaleIn 0.5s ease-out;
        }
        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .slide-in {
          animation: slideUp 0.6s ease-out;
        }
        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      
      <div className="text-center">
        <div
          className="rounded-circle bg-success d-inline-flex align-items-center justify-content-center mb-4 success-animation"
          style={{ width: "120px", height: "120px" }}
        >
          <i
            className="bi bi-check-circle text-white"
            style={{ fontSize: "60px" }}
          ></i>
        </div>

        <h1 className="fw-bold mb-3 text-success">Payment Successful!</h1>
        <h3 className="fw-bold mb-2">Order Confirmed</h3>
        <p className="text-muted fs-5 mb-4">
          Thank you for your purchase. Your order has been confirmed and is being processed.
        </p>

        {orderData && (
          <div className="slide-in">
            <div
              className="card border-0 shadow-lg p-4 rounded-4 mb-4"
              style={{ maxWidth: "600px", margin: "0 auto", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
            >
              <div className="text-white">
                <h5 className="fw-bold mb-4">Order Details</h5>
                <div className="row text-start">
                  <div className="col-12 mb-3">
                    <small className="opacity-75">Razorpay Order ID</small>
                    <div className="fw-bold font-monospace" style={{ fontSize: "0.9rem", wordBreak: "break-all" }}>
                      {orderData.razorpayOrderId || orderData.orderId}
                    </div>
                  </div>
                  <div className="col-12 mb-3">
                    <small className="opacity-75">Payment ID (Proof of Payment)</small>
                    <div className="fw-bold font-monospace" style={{ fontSize: "0.9rem", wordBreak: "break-all" }}>
                      {orderData.paymentId}
                    </div>
                  </div>
                  <div className="col-6 mb-3">
                    <small className="opacity-75">Amount Paid</small>
                    <div className="fw-bold fs-4">₹{orderData.total?.toLocaleString()}</div>
                  </div>
                  <div className="col-6 mb-3">
                    <small className="opacity-75">Payment Status</small>
                    <div className="fw-bold mt-1">
                      <span className="badge bg-success px-3 py-2">
                        <i className="bi bi-check-circle me-1"></i>PAID
                      </span>
                    </div>
                  </div>
                  <div className="col-12 mb-3">
                    <small className="opacity-75">Delivery Address</small>
                    <div className="fw-semibold">
                      {orderData.name}<br/>
                      {[orderData.houseNo, orderData.street, orderData.area].filter(Boolean).join(", ")}<br/>
                      {orderData.city}, {orderData.state} - {orderData.pincode}<br/>
                      <i className="bi bi-telephone me-1"></i>{orderData.phone}
                    </div>
                  </div>
                  <div className="col-12">
                    <small className="opacity-75">Items Ordered</small>
                    {orderData.items?.map((item, i) => (
                      <div key={i} className="fw-semibold">
                        {item.name} × {item.qty} — ₹{(item.price * item.qty).toLocaleString()}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className="card border-0 shadow-sm p-4 rounded-4 mb-4 text-start slide-in"
          style={{ maxWidth: "600px", margin: "0 auto", animationDelay: "0.2s" }}
        >
          <h6 className="fw-bold mb-3"><i className="bi bi-info-circle text-primary me-2"></i>What Happens Next?</h6>
          <div className="row">
            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="bg-success-subtle rounded-circle d-inline-flex align-items-center justify-content-center mb-2" style={{ width: "50px", height: "50px" }}>
                  <i className="bi bi-check-circle text-success" style={{ fontSize: "24px" }}></i>
                </div>
                <div className="fw-semibold">Order Confirmed</div>
                <small className="text-muted">Payment received</small>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="bg-primary-subtle rounded-circle d-inline-flex align-items-center justify-content-center mb-2" style={{ width: "50px", height: "50px" }}>
                  <i className="bi bi-box-seam text-primary" style={{ fontSize: "24px" }}></i>
                </div>
                <div className="fw-semibold">Processing</div>
                <small className="text-muted">Preparing your order</small>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="bg-warning-subtle rounded-circle d-inline-flex align-items-center justify-content-center mb-2" style={{ width: "50px", height: "50px" }}>
                  <i className="bi bi-truck text-warning" style={{ fontSize: "24px" }}></i>
                </div>
                <div className="fw-semibold">Delivery</div>
                <small className="text-muted">5-7 business days</small>
              </div>
            </div>
          </div>
          <hr className="my-3"/>
          <ul className="list-unstyled mb-0">
            <li className="mb-2">
              <i className="bi bi-check-circle-fill text-success me-2"></i>
              <span className="fw-semibold">Confirmation email sent</span> to {orderData?.email}
            </li>
            <li className="mb-2">
              <i className="bi bi-bell-fill text-primary me-2"></i>
              You'll receive updates via email and SMS
            </li>
            <li className="mb-2">
              <i className="bi bi-box-seam-fill text-info me-2"></i>
              Track your order in "My Orders" section
            </li>
          </ul>
        </div>

        <div
          className="d-grid gap-2 slide-in"
          style={{ maxWidth: "600px", margin: "0 auto", animationDelay: "0.4s" }}
        >
          <button
            className="btn btn-success btn-lg rounded-pill py-3 fw-bold mb-2"
            onClick={() => setShowBill(!showBill)}
          >
            <i className="bi bi-receipt me-2"></i>
            {showBill ? "Hide Invoice" : "View Invoice & Download"}
          </button>
          <button
            className="btn btn-primary btn-lg rounded-pill py-3 fw-bold"
            onClick={() => navigate("/my-orders")}
          >
            <i className="bi bi-box-seam me-2"></i>
            Track My Orders
          </button>
          <button
            className="btn btn-dark btn-lg rounded-pill py-3 fw-bold"
            style={{ background: "#2f0f0c" }}
            onClick={() => navigate("/perfumes")}
          >
            <i className="bi bi-bag me-2"></i>
            Continue Shopping
          </button>
          <button
            className="btn btn-outline-dark rounded-pill py-3"
            onClick={() => navigate("/")}
          >
            <i className="bi bi-house me-2"></i>
            Back to Home
          </button>
        </div>
      </div>
      
      {/* Bill Component */}
      {showBill && orderData && (
        <div className="mt-5">
          <Bill order={orderData} onDownload={handleDownloadBill} />
        </div>
      )}
    </div>
  );
};

export default OrderSuccess;
